/**
 * Custom hook for newsletter subscription
 */

import { useState } from 'react';
import { API_BASE_URL } from '../constants';
import { validateEmail } from '../utils/common';

interface UseNewsletterReturn {
  email: string;
  loading: boolean;
  success: boolean;
  error: string | null;
  setEmail: (email: string) => void;
  submit: (apiBase?: string) => Promise<void>;
  reset: () => void;
}

export function useNewsletter(apiBase?: string): UseNewsletterReturn {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (customApiBase?: string) => {
    setError(null);
    setLoading(true);

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const base = customApiBase || apiBase || API_BASE_URL;
      const res = await fetch(`${base}/api/email-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: 'newsletter',
        }),
      });

      const data = await res.json();
      if (data.success) {
        setSuccess(true);
        setEmail('');
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(data.error || 'Failed to subscribe. Please try again.');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to subscribe. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setEmail('');
    setError(null);
    setSuccess(false);
    setLoading(false);
  };

  return {
    email,
    loading,
    success,
    error,
    setEmail,
    submit,
    reset,
  };
}



